import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/utils/api';

function GmailCallback() {
  const { t } = useTranslation();
  const [status, setStatus] = useState(t('auth.processing') || 'Processing...');
  const navigate = useNavigate();
  const processedRef = useRef(false);

  useEffect(() => {
    // Prevent multiple executions (React 18 StrictMode + re-renders)
    if (processedRef.current) {
      console.log('[GmailCallback] Already processed, skipping...');
      return;
    }
    
    const processGmailAuth = async () => {
      processedRef.current = true;
      console.log('[GmailCallback] Starting Gmail OAuth process...');
      
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const state = urlParams.get('state');
        const code = urlParams.get('code');
        const error = urlParams.get('error');
        
        // Check for OAuth errors
        if (error) {
          console.error('[GmailCallback] OAuth error:', error);
          setStatus(t('auth.gmailAuthFailed') || 'Gmail-Verbindung fehlgeschlagen. Weiterleitung zu Einstellungen...');
          setTimeout(() => navigate('/settings', { replace: true }), 2000);
          return;
        }
        
        // Validate required parameters
        if (!state || !code) {
          console.error('[GmailCallback] Missing required parameters:', { state: !!state, code: !!code });
          setStatus(t('auth.gmailAuthFailed') || 'Gmail-Verbindung fehlgeschlagen. Weiterleitung zu Einstellungen...');
          setTimeout(() => navigate('/settings', { replace: true }), 2000);
          return;
        }
        
        // Validate state prefix for Gmail
        if (!state.startsWith('gmail_')) {
          console.error('[GmailCallback] Invalid state prefix for Gmail OAuth:', state);
          setStatus(t('auth.gmailAuthFailed') || 'Gmail-Verbindung fehlgeschlagen. Weiterleitung zu Einstellungen...');
          setTimeout(() => navigate('/settings', { replace: true }), 2000);
          return;
        }
        
        console.log('[GmailCallback] Processing Gmail OAuth callback...');
        setStatus(t('auth.connectingGmail') || 'Gmail wird verbunden...');
        
        // Call Gmail OAuth callback endpoint
        await api.post('/user/google-email/callback', { code, state });
        
        console.log('[GmailCallback] ✅ Gmail OAuth successful, redirecting to settings');
        setStatus(t('auth.googleEmailConnected') || 'Gmail erfolgreich verbunden! Weiterleitung zu Einstellungen...');
        setTimeout(() => navigate('/settings', { replace: true }), 1500);
        
      } catch (error) {
        console.error('[GmailCallback] ❌ Error during Gmail authentication:', error);
        setStatus(t('auth.gmailAuthFailed') || 'Gmail-Verbindung fehlgeschlagen. Weiterleitung zu Einstellungen...');
        setTimeout(() => navigate('/settings', { replace: true }), 2000);
      }
    };

    processGmailAuth();
  }, [navigate, t]); // Include dependencies

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
        <p className="text-lg text-gray-700">{status}</p>
        <p className="text-sm text-gray-500 mt-2">
          {t('auth.gmailCallbackInfo') || 'Gmail wird mit Ihrem Konto verbunden...'}
        </p>
      </div>
    </div>
  );
}

export default GmailCallback;